import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TabsBar } from '@/common/TabsBar';
import { useCourseStudyAccess } from '~/hooks/useCourseStudyAccess';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/course-study',
)({
  component: MemberCourseStudyRoute,
});

export default function MemberCourseStudyRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const { access, isLoading } = useCourseStudyAccess(memberId);

  // Resolve before deciding anything: the tab set comes from the same answer as
  // the refusal, so rendering against an unresolved query would flash the
  // student strip at a supervisor and drop their tabs on error.
  if (isLoading) return null;

  // Positive test: `access` is undefined on a 401, a 500 or a dropped
  // connection, and each of those is a refusal. `access && !access.can_view`
  // would render the strip and `<Outlet/>` on all three.
  if (access?.can_view !== true) return <NotFoundScreen />;

  // As in legacy, the first tab depends on the viewer's relation to the
  // student, not on the viewer's profile type; Quiz Submissions and
  // Certificates are always appended.
  const supervisorTabs = [
    { title: t('lessons.course-progression'), to: '' },
    { title: t('lessons.study-activities'), to: 'study-activities' },
    { title: t('lessons.quiz-submissions'), to: 'quizzes' },
    { title: t('wbs.certificates'), to: 'certificates' },
  ];
  // Plural, as legacy labels it.
  const studentTabs = [
    { title: t('lessons.courses'), to: '' },
    { title: t('lessons.quiz-submissions'), to: 'quizzes' },
    { title: t('wbs.certificates'), to: 'certificates' },
  ];

  return (
    <Container>
      <TabsBar
        tabs={access.is_supervisor ? supervisorTabs : studentTabs}
        base={'/members/$memberId/course-study'}
        tabLevel={4}
      />
      <MainContent>
        <Outlet />
      </MainContent>
    </Container>
  );
}
const Container = styled.div`
  padding-top: 6px;
`;

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  min-width: 0;
`;
